import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Button, Tabs, Tab, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Grid, IconButton, Tooltip, Chip, Alert, InputAdornment,
  Switch, Snackbar, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import StatCard from '../../components/common/StatCard';
import DataTable from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import db from '../../data/demoDatabase';
import { useAuth } from '../../context/AuthContext';

const PRIMARY = '#1B365D';
const SECONDARY = '#00B894';

const roleColors = {
  partner: { bg: '#E3F2FD', color: '#1565C0', label: 'Partner' },
  individual_agent: { bg: '#F3E5F5', color: '#7B1FA2', label: 'Individual Agent' },
  free_user: { bg: '#E8F5E9', color: '#2E7D32', label: 'Free User' },
  admin: { bg: '#FFF3E0', color: '#E65100', label: 'Admin' },
};

const inviteStatusColors = {
  sent: { bg: '#E3F2FD', color: '#1565C0', label: 'Sent' },
  opened: { bg: '#FFF8E1', color: '#F57F17', label: 'Opened' },
  submitted: { bg: '#EDE7F6', color: '#4527A0', label: 'Awaiting Approval' },
  approved: { bg: '#E8F5E9', color: '#2E7D32', label: 'Approved' },
  expired: { bg: '#FFEBEE', color: '#C62828', label: 'Expired' },
};

const generateTempPassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let pwd = '';
  for (let i = 0; i < 12; i++) pwd += chars.charAt(Math.floor(Math.random() * chars.length));
  return pwd;
};

export default function UserManagement() {
  const { user: adminUser } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  // User dialogs
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [tempPassword, setTempPassword] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', icNumber: '', role: 'partner' });

  // Invite dialogs
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteResultOpen, setInviteResultOpen] = useState(false);
  const [createdInvite, setCreatedInvite] = useState(null);
  const [inviteForm, setInviteForm] = useState({
    role: 'partner',
    recipientEmail: '',
    expiryDays: 14,
    note: '',
  });
  // Invite approval dialog (OTP simulation)
  const [approveInvite, setApproveInvite] = useState(null);
  const [approveStep, setApproveStep] = useState(0); // 0=confirm, 1=email OTP, 2=SMS OTP, 3=done

  const bump = () => setRefreshKey((k) => k + 1);

  const users = useMemo(() => db.users.getAll(), [refreshKey]);
  const invites = useMemo(() => db.invites.getAll(), [refreshKey]);

  const showSnack = (msg, severity = 'success') =>
    setSnack({ open: true, msg, severity });

  // Active tab: 0=All Users, 1=Partners, 2=Agents, 3=Free Users, 4=Invite Links
  const userTabFilters = ['all', 'partner', 'individual_agent', 'free_user'];

  const filteredUsers = useMemo(() => {
    if (activeTab === 4) return [];
    return users.filter((u) => {
      if (u.role === 'admin') return false;
      const matchesTab = activeTab === 0 || u.role === userTabFilters[activeTab];
      const matchesSearch =
        searchTerm === '' ||
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.id?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesTab && matchesSearch;
    });
  }, [users, activeTab, searchTerm]);

  const filteredInvites = useMemo(() => {
    if (activeTab !== 4) return [];
    return invites.filter((inv) => {
      if (!searchTerm) return true;
      const s = searchTerm.toLowerCase();
      return (
        inv.token?.toLowerCase().includes(s) ||
        inv.recipientEmail?.toLowerCase().includes(s) ||
        inv.submittedData?.name?.toLowerCase().includes(s)
      );
    });
  }, [invites, activeTab, searchTerm]);

  const stats = useMemo(() => {
    const relevant = users.filter((u) => u.role !== 'admin');
    return {
      total: relevant.length,
      partners: users.filter((u) => u.role === 'partner' && u.status === 'active').length,
      agents: users.filter((u) => u.role === 'individual_agent' && u.status === 'active').length,
      freeUsers: users.filter((u) => u.role === 'free_user').length,
    };
  }, [users]);

  // ─── Handlers ──────────────────────────────────────────────
  const handleCreateOpen = () => {
    setFormData({ name: '', email: '', phone: '', icNumber: '', role: 'partner' });
    setTempPassword('');
    setCreateOpen(true);
  };

  const handleCreateSubmit = () => {
    if (!formData.name || !formData.email || !formData.phone) {
      showSnack('Name, email, and phone are required', 'warning');
      return;
    }
    const pwd = generateTempPassword();
    db.users.create({
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      role: formData.role,
      icNumber: formData.icNumber,
      password: pwd,
      status: 'active',
      joinDate: new Date().toISOString().slice(0, 10),
      referralCode: `REF-${formData.name.replace(/\s/g, '').slice(0, 6).toUpperCase()}${Date.now().toString().slice(-3)}`,
    });
    setTempPassword(pwd);
    showSnack('User created successfully');
    bump();
  };

  const handleEditOpen = (row) => {
    setEditUser(row);
    setFormData({
      name: row.name || '',
      email: row.email || '',
      phone: row.phone || '',
      icNumber: row.icNumber || '',
      role: row.role || 'partner',
    });
    setEditOpen(true);
  };

  const handleEditSubmit = () => {
    if (!editUser) return;
    db.users.update(editUser.id, {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      icNumber: formData.icNumber,
      role: formData.role,
    });
    showSnack('User updated successfully');
    setEditOpen(false);
    bump();
  };

  const handleToggleStatus = (row) => {
    const newStatus = row.status === 'active' ? 'inactive' : 'active';
    db.users.update(row.id, { status: newStatus });
    showSnack(`User ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
    bump();
  };

  const handleDelete = (row) => {
    if (!window.confirm(`Delete user ${row.name}?`)) return;
    db.users.delete(row.id);
    showSnack('User deleted', 'warning');
    bump();
  };

  // ─── Invite handlers ───────────────────────────────────────
  const handleInviteOpen = () => {
    setInviteForm({ role: 'partner', recipientEmail: '', expiryDays: 14, note: '' });
    setInviteOpen(true);
  };

  const handleInviteGenerate = () => {
    const invite = db.invites.create({
      ...inviteForm,
      createdBy: adminUser?.id || 'SYSTEM',
    });
    setCreatedInvite(invite);
    setInviteOpen(false);
    setInviteResultOpen(true);
    showSnack('Invite link generated');
    bump();
  };

  const handleCopyLink = (token) => {
    const url = `https://imoney.my/register-invite?token=${token}`;
    navigator.clipboard?.writeText(url).catch(() => {});
    showSnack('Link copied to clipboard');
  };

  const handleSimulateSubmission = (inviteId) => {
    db.invites.simulateSubmission(inviteId);
    showSnack('Simulated: prospect submitted the form — now pending approval');
    bump();
  };

  // 2-step OTP approval flow
  const handleApproveStart = (inv) => {
    setApproveInvite(inv);
    setApproveStep(0);
  };
  const handleApproveNext = () => {
    if (approveStep < 2) {
      setApproveStep((s) => s + 1);
      return;
    }
    // Final step — actually create the user
    const result = db.invites.approve(approveInvite.id);
    if (result) {
      showSnack(`Account created for ${result.user.name}`);
      bump();
    }
    setApproveStep(3);
  };
  const handleApproveClose = () => {
    setApproveInvite(null);
    setApproveStep(0);
  };

  // ─── Table columns ─────────────────────────────────────────
  const userColumns = [
    { field: 'id', headerName: 'ID', width: 110 },
    { field: 'name', headerName: 'Name' },
    { field: 'email', headerName: 'Email' },
    { field: 'phone', headerName: 'Phone', width: 130 },
    {
      field: 'role', headerName: 'Role', width: 150,
      renderCell: (row) => {
        const r = roleColors[row.role] || roleColors.free_user;
        return <Chip label={r.label} size="small" sx={{ bgcolor: r.bg, color: r.color, fontWeight: 600 }} />;
      },
    },
    {
      field: 'status', headerName: 'Status', width: 110,
      renderCell: (row) => (
        <StatusBadge status={row.status === 'active' ? 'Active' : row.status === 'inactive' ? 'Inactive' : 'Pending'} />
      ),
    },
    { field: 'referralCode', headerName: 'Referral Code', width: 150 },
    { field: 'joinDate', headerName: 'Join Date', width: 120 },
    {
      field: 'actions', headerName: 'Actions', width: 150, sortable: false,
      renderCell: (row) => (
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEditOpen(row)}>
              <EditIcon fontSize="small" sx={{ color: PRIMARY }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={row.status === 'active' ? 'Deactivate' : 'Activate'}>
            <Switch checked={row.status === 'active'} size="small" onChange={() => handleToggleStatus(row)} />
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => handleDelete(row)}>
              <DeleteIcon fontSize="small" sx={{ color: '#C62828' }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const inviteColumns = [
    { field: 'id', headerName: 'ID', width: 110 },
    {
      field: 'token', headerName: 'Token', width: 200,
      renderCell: (row) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
          {row.token}
        </Typography>
      ),
    },
    {
      field: 'role', headerName: 'Role', width: 160,
      renderCell: (row) => {
        const r = roleColors[row.role] || roleColors.free_user;
        return <Chip label={r.label} size="small" sx={{ bgcolor: r.bg, color: r.color, fontWeight: 600 }} />;
      },
    },
    { field: 'recipientEmail', headerName: 'Recipient Email' },
    {
      field: 'status', headerName: 'Status', width: 160,
      renderCell: (row) => {
        const s = inviteStatusColors[row.status] || inviteStatusColors.sent;
        return <Chip label={s.label} size="small" sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600 }} />;
      },
    },
    {
      field: 'createdAt', headerName: 'Created', width: 120,
      renderCell: (row) => (
        <Typography variant="caption">{new Date(row.createdAt).toLocaleDateString()}</Typography>
      ),
    },
    {
      field: 'expiresAt', headerName: 'Expires', width: 120,
      renderCell: (row) => (
        <Typography variant="caption">{new Date(row.expiresAt).toLocaleDateString()}</Typography>
      ),
    },
    {
      field: 'actions', headerName: 'Actions', width: 260, sortable: false,
      renderCell: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Tooltip title="Copy Link">
            <IconButton size="small" onClick={() => handleCopyLink(row.token)}>
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {row.status === 'sent' && (
            <Button size="small" variant="outlined"
              sx={{ textTransform: 'none', fontSize: '0.7rem', borderRadius: 2 }}
              onClick={() => handleSimulateSubmission(row.id)}>
              Simulate Submission
            </Button>
          )}
          {row.status === 'submitted' && (
            <Button size="small" variant="contained" startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
              sx={{ backgroundColor: '#00B894', textTransform: 'none', fontSize: '0.7rem', borderRadius: 2 }}
              onClick={() => handleApproveStart(row)}>
              Approve
            </Button>
          )}
          {row.status === 'approved' && (
            <Chip label="Account Created" size="small" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, fontSize: '0.65rem' }} />
          )}
        </Box>
      ),
    },
  ];

  // ─── Render ────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY }}>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create, manage, and invite partners and agents
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<LinkIcon />} onClick={handleInviteOpen}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: PRIMARY, color: PRIMARY }}>
            Generate Invite Link
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateOpen}
            sx={{ bgcolor: PRIMARY, fontWeight: 600, borderRadius: 2, '&:hover': { bgcolor: '#142A4A' } }}>
            Create User
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Users" value={stats.total} icon={PeopleIcon} color={PRIMARY} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Active Partners" value={stats.partners} icon={BusinessIcon} color="#1565C0" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Active Agents" value={stats.agents} icon={PersonIcon} color="#7B1FA2" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Free Users" value={stats.freeUsers} icon={PersonOutlineIcon} color={SECONDARY} />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2, '& .MuiTab-root': { fontWeight: 600, textTransform: 'none' } }}>
        <Tab label="All Users" />
        <Tab label="Partners" />
        <Tab label="Individual Agents" />
        <Tab label="Free Users" />
        <Tab label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Invite Links
            {invites.filter((i) => i.status === 'submitted').length > 0 && (
              <Chip label={invites.filter((i) => i.status === 'submitted').length} size="small"
                sx={{ height: 18, fontSize: '0.65rem', backgroundColor: '#C62828', color: 'white', fontWeight: 700 }} />
            )}
          </Box>
        } />
      </Tabs>

      {/* Search */}
      <TextField placeholder={activeTab === 4 ? 'Search by token, email, or name...' : 'Search by name, email, or ID...'}
        value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
        size="small" sx={{ mb: 3, width: 400, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment>,
        }}
      />

      {/* Table */}
      {activeTab === 4 ? (
        <DataTable columns={inviteColumns} rows={filteredInvites} title="Invite Links"
          searchable={false} pageSize={10} />
      ) : (
        <DataTable columns={userColumns} rows={filteredUsers} title="Users"
          searchable={false} pageSize={10} />
      )}

      {/* Create User Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>Create New User</Box>
          <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', fontWeight: 400 }}>
            Create a partner or agent account directly
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {!tempPassword ? (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Full Name *" value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Email *" type="email" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Phone *" value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="IC Number" value={formData.icNumber}
                  onChange={(e) => setFormData({ ...formData, icNumber: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth select size="small" label="Role *" value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
                  <MenuItem value="partner">Partner</MenuItem>
                  <MenuItem value="individual_agent">Individual Agent</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 56, color: '#00B894', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>User Created</Typography>
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, textAlign: 'left' }}>
                <strong>Temporary Password (shown once):</strong>
                <Box sx={{ fontFamily: 'monospace', fontSize: '1.1rem', fontWeight: 700, mt: 0.5 }}>
                  {tempPassword}
                </Box>
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Share this password securely with the user. They should change it on first login.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setCreateOpen(false); setTempPassword(''); }} sx={{ textTransform: 'none' }}>
            Close
          </Button>
          {!tempPassword && (
            <Button variant="contained" onClick={handleCreateSubmit}
              sx={{ borderRadius: 2, textTransform: 'none', bgcolor: PRIMARY }}>
              Create User
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>Edit User</Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Full Name" value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Email" value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Phone" value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="IC Number" value={formData.icNumber}
                onChange={(e) => setFormData({ ...formData, icNumber: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth select size="small" label="Role" value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
                <MenuItem value="partner">Partner</MenuItem>
                <MenuItem value="individual_agent">Individual Agent</MenuItem>
                <MenuItem value="free_user">Free User</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit}
            sx={{ borderRadius: 2, textTransform: 'none', bgcolor: PRIMARY }}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Invite Dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>Generate Registration Invite Link</Box>
          <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', fontWeight: 400 }}>
            Send a self-registration link to a prospective partner or agent
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            The recipient will fill in their details via the link. Admin approves + OTP verifies before the account becomes active.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth select size="small" label="Role *" value={inviteForm.role}
                onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}>
                <MenuItem value="partner">Partner</MenuItem>
                <MenuItem value="individual_agent">Individual Agent</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Recipient Email (optional)" type="email"
                value={inviteForm.recipientEmail}
                onChange={(e) => setInviteForm({ ...inviteForm, recipientEmail: e.target.value })}
                helperText="Used for simulated email delivery tracking" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth select size="small" label="Link Expiry" value={inviteForm.expiryDays}
                onChange={(e) => setInviteForm({ ...inviteForm, expiryDays: Number(e.target.value) })}>
                <MenuItem value={7}>7 days</MenuItem>
                <MenuItem value={14}>14 days</MenuItem>
                <MenuItem value={30}>30 days</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} size="small" label="Note (optional)"
                value={inviteForm.note}
                onChange={(e) => setInviteForm({ ...inviteForm, note: e.target.value })}
                helperText="Internal note — e.g., source of contact" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setInviteOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" startIcon={<LinkIcon />} onClick={handleInviteGenerate}
            sx={{ borderRadius: 2, textTransform: 'none', bgcolor: PRIMARY }}>
            Generate Link
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Result Dialog */}
      <Dialog open={inviteResultOpen} onClose={() => setInviteResultOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: '#00B894' }} />
            Invite Link Generated
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {createdInvite && (
            <>
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                Share this link with the prospective {createdInvite.role === 'partner' ? 'partner' : 'agent'}.
              </Alert>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                Registration URL
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                <TextField fullWidth size="small" readOnly
                  value={`https://imoney.my/register-invite?token=${createdInvite.token}`}
                  sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'monospace', fontSize: '0.75rem', borderRadius: 2 } }}
                />
                <Button variant="outlined" startIcon={<ContentCopyIcon />}
                  onClick={() => handleCopyLink(createdInvite.token)}
                  sx={{ borderRadius: 2, textTransform: 'none', whiteSpace: 'nowrap' }}>
                  Copy
                </Button>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Token</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{createdInvite.token}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Expires</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {new Date(createdInvite.expiresAt).toLocaleDateString()}
                  </Typography>
                </Grid>
              </Grid>
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#F5F7FA', borderRadius: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Next Steps</Typography>
                <Typography variant="caption" display="block">1. Recipient fills in their details via the link</Typography>
                <Typography variant="caption" display="block">2. Admin approves from the Invite Links tab</Typography>
                <Typography variant="caption" display="block">3. OTP verification (email + SMS) simulated</Typography>
                <Typography variant="caption" display="block">4. Login account auto-created</Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button startIcon={<EmailIcon />} sx={{ textTransform: 'none' }}
            onClick={() => { showSnack('Email dispatch simulated'); }}>
            Simulate Send Email
          </Button>
          <Button variant="contained" onClick={() => setInviteResultOpen(false)}
            sx={{ borderRadius: 2, textTransform: 'none', bgcolor: PRIMARY }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Flow Dialog (OTP simulation) */}
      <Dialog open={!!approveInvite} onClose={handleApproveClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>Approve Invite & Create Account</Box>
        </DialogTitle>
        <DialogContent dividers>
          {approveInvite && approveStep === 0 && (
            <>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                Review the information submitted by the applicant before approving.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Name</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{approveInvite.submittedData?.name}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Email</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{approveInvite.submittedData?.email}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Phone</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{approveInvite.submittedData?.phone}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">IC Number</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{approveInvite.submittedData?.icNumber}</Typography></Grid>
                <Grid item xs={12}><Typography variant="caption" color="text.secondary">Company</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{approveInvite.submittedData?.company || '-'}</Typography></Grid>
                <Grid item xs={12}><Typography variant="caption" color="text.secondary">Role</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={roleColors[approveInvite.role]?.label} size="small"
                      sx={{ bgcolor: roleColors[approveInvite.role]?.bg, color: roleColors[approveInvite.role]?.color, fontWeight: 600 }} />
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
          {approveStep === 1 && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <EmailIcon sx={{ fontSize: 56, color: '#1565C0', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Email OTP Sent</Typography>
              <Typography variant="body2" color="text.secondary">
                Verification code sent to <strong>{approveInvite?.submittedData?.email}</strong>
              </Typography>
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                Demo: OTP is <strong>123456</strong> — click Next to simulate verification.
              </Alert>
            </Box>
          )}
          {approveStep === 2 && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <SendIcon sx={{ fontSize: 56, color: '#7B1FA2', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>SMS OTP Sent</Typography>
              <Typography variant="body2" color="text.secondary">
                Verification code sent to <strong>{approveInvite?.submittedData?.phone}</strong>
              </Typography>
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                Demo: OTP is <strong>123456</strong> — click Next to simulate verification.
              </Alert>
            </Box>
          )}
          {approveStep === 3 && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircleIcon sx={{ fontSize: 72, color: '#00B894', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Account Created Successfully</Typography>
              <Typography variant="body2" color="text.secondary">
                Login credentials have been emailed to the user. They can now access the portal.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {approveStep < 3 && (
            <Button onClick={handleApproveClose} sx={{ textTransform: 'none' }}>Cancel</Button>
          )}
          {approveStep < 3 && (
            <Button variant="contained" onClick={handleApproveNext}
              sx={{ borderRadius: 2, textTransform: 'none', bgcolor: approveStep === 2 ? '#00B894' : PRIMARY }}>
              {approveStep === 0 ? 'Approve & Send Email OTP' : approveStep === 1 ? 'Verify & Send SMS OTP' : 'Verify & Create Account'}
            </Button>
          )}
          {approveStep === 3 && (
            <Button variant="contained" onClick={handleApproveClose}
              sx={{ borderRadius: 2, textTransform: 'none', bgcolor: PRIMARY }}>
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}
          sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
